// How a clip SOUNDS, decided once for every reader — the room's transport,
// a seed's play page, the clip library, ghost pads, stem renders. The
// readers used to each decide for themselves, and disagreed: a MIDI clip
// with a Live bounce played the bounce AND the browser synth at once
// (docs/project/playback-audit.md §1.1).
//
// Pure: no DOM, no runtime deps, only the wire types next door.

import type { WireNote } from "../midi/notes.js";
import { MAX_DETUNE, MAX_TRANSPOSE, MAX_WARP_MARKERS, type WarpMarkerData, warpSettingsOf, type WarpSettingsData } from "../sync/protocol.js";

/** The clip fields that decide what it plays as. Structural, so a wire
 * `ClipData`, a seed snapshot clip and a web `LaneClip` all qualify. */
export interface SoundingClipLike {
  /** Absent ⇒ audio. */
  kind?: "audio" | "midi";
  /** An audio clip's object, or a MIDI clip's bounce rendered by Live. */
  audioKey?: string | null;
  /** MIDI: the flattened note list when the wire carried it. */
  notes?: readonly Pick<WireNote, "muted">[];
  /** Live's clip activator off: drawn, never scheduled. */
  muted?: boolean;
}

export type ClipSounding =
  | { kind: "audio" }
  | { kind: "synth" }
  | { kind: "silent"; reason: "deactivated" | "no-audio" | "notes-omitted" | "all-muted" };

/**
 * What one clip plays as. Exactly one of audio, synth or silence — never
 * both audio and synth.
 *
 * A MIDI clip's bounce is what Live rendered of it, so it wins while it is
 * there: the real instrument, not the browser synth. A note edit in the
 * browser drops the bounce (web-shared/src/lib/edits.ts), because Live
 * will never re-render it, and the synth takes over from the edited notes.
 */
export function clipSounding(clip: SoundingClipLike): ClipSounding {
  if (clip.muted) return { kind: "silent", reason: "deactivated" };
  if (clip.audioKey) return { kind: "audio" };
  if (clip.kind !== "midi") return { kind: "silent", reason: "no-audio" };
  if (!clip.notes || clip.notes.length === 0) return { kind: "silent", reason: "notes-omitted" };
  if (clip.notes.every((n) => n.muted)) return { kind: "silent", reason: "all-muted" };
  return { kind: "synth" };
}

/** `clipSounding(clip).kind === "audio"` — the transport's audio builders. */
export function soundsAsAudio(clip: SoundingClipLike): boolean {
  return clipSounding(clip).kind === "audio";
}

/** `clipSounding(clip).kind === "synth"` — the transport's MIDI builders. */
export function soundsAsSynth(clip: SoundingClipLike): boolean {
  return clipSounding(clip).kind === "synth";
}

// ---- Geometry: what a seed clip's settings mean for its object -------------

/** A clip's playback markers and warp/pitch settings, in the one shape every
 * reader keeps (`ClipData`'s fields, `ClipGeometry` in web-shared). */
export interface ClipGeometryData {
  isWarped: boolean;
  looping: boolean;
  startMarker: number;
  endMarker: number;
  loopStart: number;
  loopEnd: number;
  /** Live's warp mode value; Re-Pitch (3) moves pitch with speed. */
  warpMode: number;
  /** Warped clips: the tempo the markers are measured at. */
  sourceTempo?: number;
  /** Two or more: the file plays onto the grid through them. */
  warpMarkers?: WarpMarkerData[];
  /** Semitones / cents, as the browser left them. */
  transpose?: number;
  detune?: number;
  /** The warp-mode knobs, as the browser left them (`ClipData.warpSettings`). */
  warpSettings?: WarpSettingsData;
}

/** The seed-snapshot clip fields the geometry is read from (`AudioClipInfo`
 * and `SessionAudioClipInfo` in seed-history.ts). Snapshot blobs are
 * JSON-cast, never validated, so every field is read tolerantly. */
export interface SeedClipLike {
  duration: number;
  warping?: boolean;
  looping?: boolean;
  startMarker?: number;
  endMarker?: number;
  loopStart?: number;
  loopEnd?: number;
  warpMode?: { value: number };
  /** Session View clips sit in a slot; their object is the source file whole. */
  slot?: number;
  /** "source": the object is the original the clip windows into. Absent: a
   * region render of what the clip plays, settings baked in. */
  audioIs?: "source";
  sourceTempo?: number;
  warpMarkers?: ReadonlyArray<WarpMarkerData>;
  transpose?: number;
  detune?: number;
  warpSettings?: unknown;
}

export function validSourceTempo(t: unknown): t is number {
  return typeof t === "number" && Number.isFinite(t) && t >= 20 && t <= 999;
}

/** Audio at least this many beats long comes in WARPED — Live's default
 * (Auto-Warp Long Samples on, Loop/Warp Short Samples on Auto): everything
 * is warped except a hit too short to be a loop, and Live's line is bars,
 * not seconds. A seed's render at the Set's tempo, a dropped file at its
 * own or the room's: at that tempo warping is the identity, no stretch and
 * no render, so it costs nothing until the tempo or the pitch moves, and
 * then the clip follows keeping its pitch (Live's Complex). Under a bar it
 * is a hit: unwarped, at its own speed. */
export const AUTO_WARP_MIN_BEATS = 4;

/** Whether audio of this many beats (at the tempo it would be warped at)
 * is auto-warped. */
export function autoWarps(durationBeats: number): boolean {
  return durationBeats >= AUTO_WARP_MIN_BEATS - 1e-6;
}
/** Live's Complex warp mode: stretch keeping pitch, for any material. */
export const WARP_MODE_COMPLEX = 4;

/**
 * How a seed clip's object plays. THE rule, for the room (hydrate), the seed
 * page (`fromSnapshot`), the clip library and every card and pad built off
 * a snapshot — they used to each read the settings their own way and play
 * the same clip differently (docs/project/playback-audit.md §3).
 *
 * A TIMELINE clip uploaded from a seed owns a REGION RENDER of its span: warp,
 * trims and loop are already in the bytes, so it plays flat — unwarped,
 * unlooped, from the object's top. Live would otherwise apply the source
 * file's markers to the render. (A jam's region render stays warped: see
 * extension clip-audio.ts adoptWireAudio.) A SESSION clip ships its source file whole,
 * and so does a timeline clip a browser saved (`audioIs: "source"`): the
 * settings are what make the object sound right, and they are kept.
 *
 * A source clip that is warped but names no tempo is measured on the SET's
 * grid: its beats were laid out at `setTempo`, so that is what carries it —
 * the same value the merge engine stamps on a warped create that carries
 * none. An unwarped one-shot plays at its file's own speed, as Live plays it.
 */
export function seedClipGeometry(clip: SeedClipLike, setTempo: number): ClipGeometryData {
  const source = clip.slot !== undefined || clip.audioIs === "source";
  const warpMode = clip.warpMode?.value ?? 0;
  if (!source) {
    // A render plays flat — and, from a bar up, warped at the Set's tempo
    // it was rendered at: the same sound, until the tempo or the pitch
    // moves (`autoWarps`).
    const warp = validSourceTempo(setTempo) && autoWarps(clip.duration);
    return {
      isWarped: warp,
      looping: false,
      startMarker: 0,
      endMarker: clip.duration,
      loopStart: 0,
      loopEnd: clip.duration,
      warpMode: warp ? WARP_MODE_COMPLEX : warpMode,
      ...(warp ? { sourceTempo: setTempo } : {}),
    };
  }
  const warped = clip.warping ?? false;
  const markers = clip.warpMarkers;
  const tempo = validSourceTempo(clip.sourceTempo) ? clip.sourceTempo : warped && validSourceTempo(setTempo) ? setTempo : undefined;
  return {
    isWarped: warped,
    looping: clip.looping ?? false,
    startMarker: clip.startMarker ?? 0,
    endMarker: clip.endMarker ?? clip.duration,
    loopStart: clip.loopStart ?? 0,
    loopEnd: clip.loopEnd ?? clip.duration,
    warpMode,
    ...(tempo !== undefined ? { sourceTempo: tempo } : {}),
    ...(warped && markers && markers.length >= 2 && markers.length <= MAX_WARP_MARKERS
      ? { warpMarkers: markers.map((m) => ({ beat: m.beat, sec: m.sec })) }
      : {}),
    ...(Number.isInteger(clip.transpose) && clip.transpose !== 0 && Math.abs(clip.transpose!) <= MAX_TRANSPOSE
      ? { transpose: clip.transpose! }
      : {}),
    ...(typeof clip.detune === "number" && Number.isFinite(clip.detune) && clip.detune !== 0 && Math.abs(clip.detune) <= MAX_DETUNE
      ? { detune: clip.detune }
      : {}),
    ...warpSettingsField(clip.warpSettings),
  };
}

/** A valid, non-empty `warpSettings` as a field to spread; nothing otherwise. */
function warpSettingsField(v: unknown): { warpSettings?: WarpSettingsData } {
  const s = v === undefined ? null : warpSettingsOf(v);
  return s && Object.keys(s).length > 0 ? { warpSettings: s } : {};
}
